/**
 * Enhanced Color System for WebsiteBuilderV2
 * Implements the exact color formula from Figma design system
 * Based on the color-system-documentation.md specifications
 */

/**
 * Applies overlay formula exactly as specified in Figma
 * @param {string} baseColor - Base color in hex format
 * @param {number} blackPercent - Black overlay percentage (0-100)
 * @param {number} whitePercent - White overlay percentage (0-100)
 * @returns {string} Resulting color in hex format
 */
export const applyOverlayFormula = (baseColor, blackPercent, whitePercent) => {
  // Convert hex to RGB
  const hex = baseColor.replace("#", "")
  const r = parseInt(hex.substr(0, 2), 16)
  const g = parseInt(hex.substr(2, 2), 16)
  const b = parseInt(hex.substr(4, 2), 16)

  // Apply black overlay (darken)
  const blackMultiplier = 1 - blackPercent / 100
  let newR = Math.round(r * blackMultiplier)
  let newG = Math.round(g * blackMultiplier)
  let newB = Math.round(b * blackMultiplier)

  // Apply white overlay (lighten)
  const whiteMultiplier = whitePercent / 100
  newR = Math.round(newR + (255 - newR) * whiteMultiplier)
  newG = Math.round(newG + (255 - newG) * whiteMultiplier)
  newB = Math.round(newB + (255 - newB) * whiteMultiplier)

  // Convert back to hex
  const toHex = (n) => {
    const hex = n.toString(16)
    return hex.length === 1 ? "0" + hex : hex
  }

  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`
}

/**
 * Color presets exactly as defined in Figma
 */
export const COLOR_PRESETS = {
  earthy: {
    id: "earthy",
    name: "Earthy",
    description: "Warm, grounded tones inspired by natural clay and soil.",
    primaryColor: "#BD654F",
    colors: {
      primary: "#BD654F",
      bgDark: "#A4786E",
      bgMedium: "#E6DAD7",
      bgLight: "#F4ECE9",
      bgExtraLight: "#F8F6F5",
      textDark: "#454A4F"
    }
  },
  zen: {
    id: "zen",
    name: "Zen",
    description: "Calm, muted greens for peaceful, mindful spaces.",
    primaryColor: "#97854A",
    colors: {
      primary: "#97854A",
      bgDark: "#91896B",
      bgMedium: "#E1DFD6",
      bgLight: "#F1EFE9",
      bgExtraLight: "#F7F7F5",
      textDark: "#454A4F"
    }
  },
  tranquility: {
    id: "tranquility",
    name: "Tranquility",
    description: "Cool, airy shades that soothe and relax instantly.",
    primaryColor: "#5991B7",
    colors: {
      primary: "#5991B7",
      bgDark: "#738EA1",
      bgMedium: "#D8E1E5",
      bgLight: "#EAF0F4",
      bgExtraLight: "#F6F7F8",
      textDark: "#454A4F"
    }
  },
  radiance: {
    id: "radiance",
    name: "Radiance",
    description: "Warm, golden hues that brighten and uplift.",
    primaryColor: "#C5782D",
    colors: {
      primary: "#C5782D",
      bgDark: "#A7825D",
      bgMedium: "#E7DDD3",
      bgLight: "#F5EDE6",
      bgExtraLight: "#F8F6F5",
      textDark: "#454A4F"
    }
  },
  vitality: {
    id: "vitality",
    name: "Vitality",
    description: "Bold, energetic pops of color that inspire action.",
    primaryColor: "#BA1457",
    colors: {
      primary: "#BA1457",
      bgDark: "#A25171",
      bgMedium: "#E6CFD8",
      bgLight: "#F4E4EA",
      bgExtraLight: "#F8F4F6",
      textDark: "#543340"
    }
  },
  awaken: {
    id: "awaken",
    name: "Awaken",
    description: "Fresh, lively coral tones that spark new beginnings.",
    primaryColor: "#E66565",
    colors: {
      primary: "#E66565",
      bgDark: "#B87878",
      bgMedium: "#ECDADA",
      bgLight: "#F8ECEC",
      bgExtraLight: "#F9F6F6",
      textDark: "#5D4343"
    }
  },
  flourish: {
    id: "flourish",
    name: "Flourish",
    description: "Rich greens that nurture growth and balance.",
    primaryColor: "#627654",
    colors: {
      primary: "#627654",
      bgDark: "#778170",
      bgMedium: "#D9DCD7",
      bgLight: "#EBEDEA",
      bgExtraLight: "#F6F6F6",
      textDark: "#424640"
    }
  },
  harmony: {
    id: "harmony",
    name: "Harmony",
    description: "Balanced teal tones for unity and calm connection.",
    primaryColor: "#3F978E",
    colors: {
      primary: "#3F978E",
      bgDark: "#66918D",
      bgMedium: "#D5E1E0",
      bgLight: "#E8F1F0",
      bgExtraLight: "#F5F7F7",
      textDark: "#3B4D4B"
    }
  },
  balance: {
    id: "balance",
    name: "Balance",
    description: "Deep, steady blues for focus and inner stability.",
    primaryColor: "#234188",
    colors: {
      primary: "#234188",
      bgDark: "#58678A",
      bgMedium: "#D1D6DF",
      bgLight: "#E6E8EF",
      bgExtraLight: "#F5F5F7",
      textDark: "#363C4A"
    }
  },
  serenity: {
    id: "serenity",
    name: "Serenity",
    description: "Soft purples that invite quiet reflection and rest.",
    primaryColor: "#735097",
    colors: {
      primary: "#735097",
      bgDark: "#7F6E91",
      bgMedium: "#DCD7E1",
      bgLight: "#EDE9F1",
      bgExtraLight: "#F6F6F7",
      textDark: "#463F4D"
    }
  }
}

/**
 * Neutral colors as defined in Figma
 */
export const NEUTRAL_COLORS = {
  stroke: "#E6E6E6",
  strokeInput: "#DEDEDE",
  white: "#FFFFFF",
  black: "#000000",
  greyLight: "#FAF9F9",
  grey: "#55778A",
  greyMedium: "#394D59",
  greyDark: "#26353D"
}

/**
 * Special colors as defined in Figma
 */
export const SPECIAL_COLORS = {
  reviewsStars: "#FF9529"
}

/**
 * Generates a complete color palette using the exact Figma formula
 * @param {string} primaryColor - Base color in hex format
 * @returns {object} Complete color palette
 */
export const generateColorPalette = (primaryColor) => ({
  // Primary color (no overlays)
  primary: primaryColor,

  // Background variations using exact Figma formula
  bgDark: applyOverlayFormula(primaryColor, 30, 30), // Black: 30%, White: 30%
  bgMedium: applyOverlayFormula(primaryColor, 8, 85), // Black: 8%, White: 85%
  bgLight: applyOverlayFormula(primaryColor, 2, 90), // Black: 2%, White: 90%
  bgExtraLight: "#f6f6f6", // Fixed neutral color for consistency

  // Text color using our custom base color
  textDark: "#454A4F", // Custom text color for better readability

  // Additional colors for compatibility
  primaryDark: applyOverlayFormula(primaryColor, 20, 0), // For compatibility
  secondary: applyOverlayFormula(primaryColor, 5, 95), // For compatibility
  secondaryDark: applyOverlayFormula(primaryColor, 10, 80), // For compatibility
  accent: applyOverlayFormula(primaryColor, 10, 80), // For compatibility
  background: applyOverlayFormula(primaryColor, 2, 98), // Alias for bgLight
  textLight: "#FFFFFF", // Always white for contrast
  strokeMedium: applyOverlayFormula(primaryColor, 15, 70), // For compatibility

  // Promo banner colors
  promoBannerBackground: applyOverlayFormula(primaryColor, 5, 95),
  promoBannerText: applyOverlayFormula(primaryColor, 70, 0),
  promoBannerLink: primaryColor,

  // Neutral colors
  ...NEUTRAL_COLORS,

  // Special colors
  reviewStars: SPECIAL_COLORS.reviewsStars
})

/**
 * Gets all available color presets
 * @returns {array} Array of color preset objects
 */
export const getColorPresets = () => Object.values(COLOR_PRESETS)

/**
 * Gets a specific color preset by ID
 * @param {string} presetId - Preset ID
 * @returns {object|null} Color preset object or null if not found
 */
export const getColorPreset = (presetId) => COLOR_PRESETS[presetId] || null

/**
 * Validates if a color string is a valid hex color
 * @param {string} color - Color string to validate
 * @returns {boolean} true if valid hex color
 */
export const isValidHexColor = (color) => {
  if (!color || typeof color !== "string") return false
  const cleanColor = color.replace("#", "")
  return /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(cleanColor)
}

/**
 * Gets a safe fallback color if the provided color is invalid
 * @param {string} color - Color to check
 * @param {string} fallback - Fallback color
 * @returns {string} Valid color string
 */
export const getSafeColor = (color, fallback = "#FFFFFF") => (isValidHexColor(color) ? color : fallback)

/**
 * Converts a hex color to RGB values
 * @param {string} hex - Hex color string (e.g., "#FF0000" or "FF0000")
 * @returns {object} RGB object with r, g, b values
 */
export const hexToRgb = (hex) => {
  const cleanHex = hex.replace("#", "")

  if (cleanHex.length === 3) {
    const r = parseInt(cleanHex[0] + cleanHex[0], 16)
    const g = parseInt(cleanHex[1] + cleanHex[1], 16)
    const b = parseInt(cleanHex[2] + cleanHex[2], 16)
    return { r, g, b }
  }

  if (cleanHex.length === 6) {
    const r = parseInt(cleanHex.substring(0, 2), 16)
    const g = parseInt(cleanHex.substring(2, 4), 16)
    const b = parseInt(cleanHex.substring(4, 6), 16)
    return { r, g, b }
  }

  return { r: 0, g: 0, b: 0 }
}

/**
 * Calculates the relative luminance of a color
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {number} Relative luminance (0-1)
 */
export const getRelativeLuminance = (r, g, b) => {
  const [rs, gs, bs] = [r, g, b].map((c) => {
    c = c / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

/**
 * Determines if a color is dark or light based on its luminance
 * @param {string} color - Color in hex format (e.g., "#FF0000")
 * @returns {boolean} true if dark, false if light
 */
export const isDarkColor = (color) => {
  if (!color) return false

  const rgb = hexToRgb(color)
  const luminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b)

  return luminance < 0.5
}

/**
 * Gets the appropriate text color based on background color
 * @param {string} backgroundColor - Background color in hex format
 * @param {object} theme - Theme object with color properties
 * @param {object} options - Additional options
 * @returns {string} Appropriate text color
 */
export const getContrastingTextColor = (backgroundColor, theme = {}, options = {}) => {
  const {
    forceLight = false,
    forceDark = false,
    lightTextColor = theme?.colors?.textLight || "#FFFFFF",
    darkTextColor = theme?.colors?.textDark || "#1F2937"
  } = options

  if (forceLight) return lightTextColor
  if (forceDark) return darkTextColor
  if (!backgroundColor) return darkTextColor

  const isDark = isDarkColor(backgroundColor)
  return isDark ? lightTextColor : darkTextColor
}

/**
 * Gets text color for section based on various factors
 * @param {object} section - Section object
 * @param {object} theme - Theme object
 * @param {object} options - Additional options
 * @returns {string} Appropriate text color
 */
export const getSectionTextColor = (section, theme = {}, options = {}) => {
  const settings = section?.settings || {}
  const { hasBackgroundImage = false, isInWhiteContainer = false, layout = null } = options

  if (isInWhiteContainer) {
    return getContrastingTextColor(null, theme, { forceDark: true })
  }

  if (hasBackgroundImage || layout === "full_image" || (layout && layout.includes("image"))) {
    return getContrastingTextColor(null, theme, { forceLight: true })
  }

  const showBackgroundColor = settings.showBackgroundColor || false
  const backgroundColor = settings.backgroundColor

  if (showBackgroundColor && backgroundColor) {
    return getContrastingTextColor(backgroundColor, theme)
  }

  const themeBackgroundColor = theme?.colors?.background || theme?.colors?.bgLight

  if (themeBackgroundColor) {
    return getContrastingTextColor(themeBackgroundColor, theme)
  }

  return getContrastingTextColor(null, theme, { forceDark: true })
}

/**
 * Gets text color for specific text elements within a section
 * @param {string} elementType - Type of text element (heading, body, caption, etc.)
 * @param {string} backgroundColor - Background color
 * @param {object} theme - Theme object
 * @param {object} options - Additional options
 * @returns {string} Appropriate text color
 */
export const getElementTextColor = (elementType, backgroundColor, theme = {}, options = {}) => {
  const {
    lightTextColor = theme?.colors?.textLight || "#FFFFFF",
    darkTextColor = theme?.colors?.textDark || "#454A4F"
  } = options

  switch (elementType) {
    case "heading":
      return getContrastingTextColor(backgroundColor, theme, {
        lightTextColor: theme?.colors?.headingLight || lightTextColor,
        darkTextColor: theme?.colors?.headingDark || darkTextColor
      })
    case "body":
      return getContrastingTextColor(backgroundColor, theme, {
        lightTextColor: theme?.colors?.bodyLight || lightTextColor,
        darkTextColor: theme?.colors?.bodyDark || darkTextColor
      })
    case "caption":
      return getContrastingTextColor(backgroundColor, theme, {
        lightTextColor: theme?.colors?.captionLight || lightTextColor,
        darkTextColor: theme?.colors?.captionDark || darkTextColor
      })
    default:
      return getContrastingTextColor(backgroundColor, theme)
  }
}
